/* eslint-disable sonarjs/pseudo-random */
import { http, HttpResponse } from 'msw';

import { env } from '@/config/env';
import { axios } from '@/lib/axios';
import { networkDelay } from '@/test/mocks/utils';

export type GetAvailableBatchesParams = {
  profile_id: string;
  variant_id: string;
  country: string;
};

export type SessionDetail = {
  day: string;
  timeZone: string;
  time: string;
};

export type BatchSchedule = {
  end_date: string;
  session_details: SessionDetail[];
  start_time: string;
  batch_days: string[];
  start_date: string;
};

export type Batch = {
  batch_id: string;
  version: string;
  allowed_countries: string[];
  batch_status: string;
  capacity: string;
  course: string;
  created_by: string;
  created_on: string;
  grade: string;
  name: string;
  schedule: BatchSchedule;
  strength: string;
  batch_type: string;
  product_id: string;
  tags: string[];
  remaining_capacity: string;
  capacity_status: string;
  metadata?: {
    completed_session_count?: number;
  };
  start_date_formatted: string;
  schedule_summary: string;
  display_summary: string;
};

export type OngoingVoucher = {
  amount: number | string;
  currency_prefix: string;
  label: string;
};

export type GetAvailableBatchesResponse = {
  status: string;
  message: string;
  recommended_batches: Batch[];
  other_batches: Batch[];
  total_count: number;
  ongoing_voucher?: OngoingVoucher;
};

export const generateMockBatch = (_id: number): Batch => {
  const batchNames = ['IMW-US', 'IMW-IN', 'IMW-CA', 'DMW-US', 'DMW-IN'];
  const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const times = ['09:00', '12:00', '15:00', '18:00', '20:00'];
  const tags = ['New Batches', 'Starting Soon', 'Fast Filling'];

  const randomBatchId = crypto.randomUUID().replaceAll('-', '');
  const randomSuffix = Math.floor(1000 + Math.random() * 9000);
  const randomName = `${batchNames[Math.floor(Math.random() * batchNames.length)]}-${randomSuffix}`;
  const randomDays = Array.from(
    { length: Math.floor(Math.random() * 3) + 2 },
    () => days[Math.floor(Math.random() * days.length)]
  ).slice(0, 2);
  const randomTimes = randomDays.map(
    () => times[Math.floor(Math.random() * times.length)]
  );
  const randomStartDate = new Date(
    Date.now() + Math.random() * 90 * 24 * 60 * 60 * 1000
  );

  return {
    batch_id: randomBatchId,
    version: Math.floor(Math.random() * 999_999_999).toString(),
    allowed_countries: ['IN'],
    batch_status: 'active',
    capacity: '5',
    course: '1-30',
    created_by: 'system@example.com',
    created_on: Math.floor(Date.now() / 1000).toString(),
    grade: '2-3',
    name: randomName,
    schedule: {
      end_date: new Date(randomStartDate.getTime() + 60 * 24 * 60 * 60 * 1000)
        .toISOString()
        .split('T')[0],
      session_details: randomDays.map((day, i) => ({
        day,
        timeZone: 'Asia/Kolkata',
        time: randomTimes[i],
      })),
      start_time: Math.floor(randomStartDate.getTime() / 1000).toString(),
      batch_days: randomDays,
      start_date: randomStartDate.toISOString().split('T')[0],
    },
    strength: '0',
    batch_type: 'batches',
    product_id: crypto.randomUUID(),
    tags: [tags[Math.floor(Math.random() * tags.length)]],
    remaining_capacity: '5',
    capacity_status: 'Available',
    metadata: undefined,
    start_date_formatted: `${randomStartDate.toISOString().split('T')[0]} ${randomTimes[0]}`,
    schedule_summary: `${randomDays.join(', ')} at ${randomTimes.join(', ')}`,
    display_summary: `${randomDays.join(', ')} at ${randomTimes.join(', ')} - Available`,
  };
};

export const getAvailableBatches = (
  params: GetAvailableBatchesParams
): Promise<GetAvailableBatchesResponse> => {
  return axios.get('/batches/v1/onboarding/available-batches', {
    params,
  });
};

const ENGLISH_VARIANT_ID = 'bdd2f01b-f690-462c-9a66-933b4dfcc91b';

export const getAvailableBatchesMockHandler = http.get(
  `${env.API_URL}/batches/v1/onboarding/available-batches`,
  async ({ request }): Promise<HttpResponse<GetAvailableBatchesResponse>> => {
    await networkDelay();

    const url = new URL(request.url);
    const variantId = url.searchParams.get('variant_id');
    const isEnglish = variantId === ENGLISH_VARIANT_ID;

    const ongoingBatches = isEnglish
      ? []
      : Array.from({ length: 2 }, (_, i) => {
          const batch = generateMockBatch(i);
          batch.tags = i === 0 ? ['Ongoing', 'Fast Filling'] : ['Ongoing'];
          batch.metadata = { completed_session_count: 2 };
          return batch;
        });
    const otherRecommendedBatches = Array.from(
      { length: isEnglish ? 11 : 9 },
      (_, i) => generateMockBatch(i + ongoingBatches.length)
    );
    const recommendedBatches = [...ongoingBatches, ...otherRecommendedBatches];
    const otherBatches = Array.from({ length: 0 }, (_, i) =>
      generateMockBatch(i + 11)
    );

    const mockData: GetAvailableBatchesResponse = {
      status: 'success',
      message: `Found ${recommendedBatches.length} recommended and ${otherBatches.length} other available batches`,
      recommended_batches: recommendedBatches,
      other_batches: otherBatches,
      total_count: recommendedBatches.length + otherBatches.length,
      ongoing_voucher: {
        amount: 50,
        currency_prefix: 'US$',
        label: 'US$50',
      },
    };

    return HttpResponse.json(mockData);
  }
);
